import { type FormEvent, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { can } from '../lib/permissions';
import type { ReleasePacket, Version } from '../lib/types';
import { useStore } from '../store/useStore';

export function Releases() {
  const versions = useStore((s) => s.versions);
  const access = useStore((s) => s.access);
  const syncState = useStore((s) => s.syncState);

  const [creating, setCreating] = useState(false);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const drafts = versions.filter((v) => v.status === 'draft' && !v.deletedAt);
  const released = versions
    .filter((v) => v.status === 'released' && !v.deletedAt)
    .sort((a, b) => (b.releasedAt ?? '').localeCompare(a.releasedAt ?? ''));

  const offline = syncState === 'offline';
  // Reading the history and cutting a release are separate: the page itself is a
  // section an admin grants, the release button is a permission on top of it.
  const mayManage = can(access, 'version.manage');

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold tracking-tight">Releases</h1>
          <p className="mt-0.5 text-sm text-ink-faint">
            Bundle completed tasks into a version and post the changelog to Discord.
          </p>
        </div>
        {mayManage && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="btn btn-primary"
            disabled={offline}
            title={offline ? 'Releases need a connection to the server' : undefined}
          >
            New version
          </button>
        )}
      </div>

      {offline && (
        <p className="rounded-lg border border-blocked/40 bg-blocked/10 px-3 py-2 text-sm text-blocked">
          You're offline. Releases are disabled until the connection comes back.
        </p>
      )}

      {drafts.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="text-xs font-semibold tracking-wide text-ink-soft uppercase">Drafts</h2>
          {drafts.map((version) => (
            <div key={version.id} className="card flex items-center gap-3 p-4">
              <span className="font-mono text-sm font-medium">{version.versionNumber}</span>
              {version.title && <span className="truncate text-sm text-ink-soft">{version.title}</span>}
              <span className="ml-auto flex gap-2">
                {mayManage && (
                  <button
                    type="button"
                    onClick={() => setReleasingId(version.id)}
                    className="btn btn-primary !py-1 !text-xs"
                    disabled={offline}
                  >
                    Review &amp; release
                  </button>
                )}
              </span>
            </div>
          ))}
        </section>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="text-xs font-semibold tracking-wide text-ink-soft uppercase">History</h2>
        {released.length === 0 ? (
          <div className="card p-8 text-center">
            <p className="text-sm font-medium">Nothing released yet</p>
            <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-ink-faint">
              Create a version, then release it to sweep every completed task into a packet and
              generate its changelog.
            </p>
          </div>
        ) : (
          released.map((version) => (
            <button
              key={version.id}
              type="button"
              onClick={() => setViewingId(version.id)}
              className="card flex items-center gap-3 p-4 text-left transition-colors hover:border-line-strong hover:bg-elevated"
            >
              <span className="font-mono text-sm font-medium">{version.versionNumber}</span>
              {version.title && (
                <span className="truncate text-sm text-ink-soft">{version.title}</span>
              )}
              <span className="ml-auto flex shrink-0 items-center gap-3 text-xs text-ink-faint">
                {version.taskCount !== undefined && <span>{version.taskCount} tasks</span>}
                {version.releasedAt && <span>{version.releasedAt.slice(0, 10)}</span>}
              </span>
            </button>
          ))
        )}
      </section>

      {creating && <CreateVersionDialog onClose={() => setCreating(false)} />}
      {releasingId && (
        <ReleaseDialog versionId={releasingId} onClose={() => setReleasingId(null)} />
      )}
      {viewingId && <ChangelogDialog versionId={viewingId} onClose={() => setViewingId(null)} />}
    </div>
  );
}

function useModal(onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    ref.current?.showModal();
  }, []);
  return {
    ref,
    dialogProps: {
      onClose,
      onClick: (event: React.MouseEvent) => {
        if (event.target === ref.current) ref.current?.close();
      },
      className:
        'm-auto w-[min(38rem,calc(100vw-2rem))] max-h-[85dvh] overflow-auto rounded-xl border border-line bg-surface p-0 text-ink backdrop:bg-black/60 backdrop:backdrop-blur-sm',
    },
  };
}

function CreateVersionDialog({ onClose }: { onClose: () => void }) {
  const activeGroupId = useStore((s) => s.activeGroupId);
  const refresh = useStore((s) => s.refresh);
  const { ref, dialogProps } = useModal(onClose);

  const [versionNumber, setVersionNumber] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/api/groups/${activeGroupId}/versions`, {
        method: 'POST',
        body: { versionNumber, title },
      });
      await refresh();
      ref.current?.close();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create the version');
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog ref={ref} {...dialogProps}>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        <h2 className="text-sm font-semibold">New version</h2>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-ink-soft">Version number</span>
          <input
            className="field-input font-mono text-sm"
            value={versionNumber}
            onChange={(e) => setVersionNumber(e.target.value)}
            placeholder="1.4.0"
            required
            maxLength={40}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-ink-soft">
            Title <span className="text-ink-faint">(optional)</span>
          </span>
          <input
            className="field-input text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Combat rework"
            maxLength={200}
          />
        </label>

        {error && (
          <p className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button type="button" onClick={() => ref.current?.close()} className="btn btn-ghost">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * The release step. It previews exactly what will be bundled before anything is
 * written, because releasing is not reversible.
 */
function ReleaseDialog({ versionId, onClose }: { versionId: string; onClose: () => void }) {
  const activeGroupId = useStore((s) => s.activeGroupId);
  const fields = useStore((s) => s.fields);
  const refresh = useStore((s) => s.refresh);
  const { ref, dialogProps } = useModal(onClose);

  const [description, setDescription] = useState('');
  const [includeUnassigned, setIncludeUnassigned] = useState(true);
  const [notify, setNotify] = useState(true);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const [packet, setPacket] = useState<ReleasePacket | null>(null);
  const [changelog, setChangelog] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ taskCount: number; sent: number; failed: number } | null>(null);

  const fieldIds = selectedFields.length > 0 ? selectedFields : undefined;

  // Re-preview whenever the configuration changes, so the summary always matches
  // the buttons as they currently sit.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await api<{ packet: ReleasePacket; changelog: string }>(
          `/api/groups/${activeGroupId}/versions/${versionId}/preview`,
          { method: 'POST', body: { description, includeUnassigned, fieldIds } },
        );
        if (!cancelled) {
          setPacket(result.packet);
          setChangelog(result.changelog);
        }
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Preview failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeGroupId, versionId, description, includeUnassigned, selectedFields.join(',')]);

  async function release() {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{
        taskCount: number;
        deliveries: Array<{ ok: boolean }>;
      }>(`/api/groups/${activeGroupId}/versions/${versionId}/release`, {
        method: 'POST',
        body: { description, includeUnassigned, fieldIds, notify },
      });
      await refresh();
      setDone({
        taskCount: result.taskCount,
        sent: result.deliveries.filter((d) => d.ok).length,
        failed: result.deliveries.filter((d) => !d.ok).length,
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Release failed');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <dialog ref={ref} {...dialogProps}>
        <div className="flex flex-col gap-4 p-5 text-center">
          <p className="text-sm font-medium text-done">Released</p>
          <p className="text-sm text-ink-soft">
            {done.taskCount} task{done.taskCount === 1 ? '' : 's'} bundled.{' '}
            {done.sent > 0 && `Posted to ${done.sent} Discord channel${done.sent === 1 ? '' : 's'}.`}
            {done.failed > 0 && (
              <span className="text-blocked">
                {' '}
                {done.failed} webhook{done.failed === 1 ? '' : 's'} failed — check Settings.
              </span>
            )}
          </p>
          <button type="button" onClick={() => ref.current?.close()} className="btn btn-primary">
            Close
          </button>
        </div>
      </dialog>
    );
  }

  return (
    <dialog ref={ref} {...dialogProps}>
      <div className="flex flex-col gap-5 p-5">
        <h2 className="text-sm font-semibold">
          Release {packet?.versionNumber ?? ''}
        </h2>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-ink-soft">
            Release notes <span className="text-ink-faint">(optional)</span>
          </span>
          <textarea
            className="field-input resize-y text-sm leading-relaxed"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this release is about, in a sentence or two."
            maxLength={10_000}
          />
        </label>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-xs font-medium text-ink-soft">
            Dev fields <span className="text-ink-faint">(none selected = all)</span>
          </legend>
          <div className="flex flex-wrap gap-2">
            {fields.map((field) => {
              const on = selectedFields.includes(field.id);
              return (
                <button
                  key={field.id}
                  type="button"
                  onClick={() =>
                    setSelectedFields((current) =>
                      on ? current.filter((id) => id !== field.id) : [...current, field.id],
                    )
                  }
                  className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                    on
                      ? 'border-accent bg-accent/15 text-ink'
                      : 'border-line text-ink-soft hover:bg-hover'
                  }`}
                >
                  <span className="size-1.5 rounded-full" style={{ backgroundColor: field.color }} />
                  {field.name}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="flex flex-col gap-2.5">
          <Toggle
            checked={includeUnassigned}
            onChange={setIncludeUnassigned}
            label="Include completed tasks with no assignee"
          />
          <Toggle checked={notify} onChange={setNotify} label="Post the changelog to Discord" />
        </div>

        <div className="rounded-lg border border-line bg-canvas p-3">
          <p className="mb-2 text-xs font-medium text-ink-soft">
            Preview — {packet?.taskCount ?? 0} task{packet?.taskCount === 1 ? '' : 's'}
          </p>
          <pre className="max-h-52 overflow-auto font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-ink-soft">
            {changelog || 'Loading…'}
          </pre>
        </div>

        {error && (
          <p className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between border-t border-line pt-4">
          <p className="text-xs text-ink-faint">Releasing cannot be undone.</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => ref.current?.close()} className="btn btn-ghost">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void release()}
              className="btn btn-primary"
              disabled={busy}
            >
              {busy ? 'Releasing…' : 'Release'}
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
}

function ChangelogDialog({ versionId, onClose }: { versionId: string; onClose: () => void }) {
  const activeGroupId = useStore((s) => s.activeGroupId);
  const { ref, dialogProps } = useModal(onClose);
  const [version, setVersion] = useState<Version | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void api<{ version: Version }>(`/api/groups/${activeGroupId}/versions/${versionId}`)
      .then((result) => setVersion(result.version))
      .catch(() => undefined);
  }, [activeGroupId, versionId]);

  async function copy() {
    if (!version?.changelog) return;
    await navigator.clipboard.writeText(version.changelog).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  return (
    <dialog ref={ref} {...dialogProps}>
      <div className="flex flex-col gap-4 p-5">
        <div className="flex items-center gap-3">
          <h2 className="font-mono text-sm font-semibold">{version?.versionNumber}</h2>
          {version?.title && <span className="truncate text-sm text-ink-soft">{version.title}</span>}
          <button type="button" onClick={() => void copy()} className="btn btn-ghost ml-auto !py-1 !text-xs">
            {copied ? 'Copied' : 'Copy markdown'}
          </button>
        </div>

        <pre className="max-h-[55dvh] overflow-auto rounded-lg border border-line bg-canvas p-3 font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-ink-soft">
          {version?.changelog ?? 'Loading…'}
        </pre>

        <div className="flex justify-end border-t border-line pt-4">
          <button type="button" onClick={() => ref.current?.close()} className="btn btn-ghost">
            Close
          </button>
        </div>
      </div>
    </dialog>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="size-4 shrink-0 accent-accent"
      />
      <span className="text-ink-soft">{label}</span>
    </label>
  );
}
